// ============================================================================
// TournamentBustModal - bust handling for tournament players
// ============================================================================
// Replaces the cash "Reload Chips" dialog (which shows NaN values, references
// wallet min/max buy-ins, and is generally wrong for tournament play).
// Shown when the local player's tournament seat hits 0 chips. Surfaces the
// rebuy option with proper context: cost, chip stack returned, late-reg
// countdown, and what happens if they don't rebuy.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Text, VStack, HStack, Button, Badge,
} from '@chakra-ui/react';
import { Skull, RotateCcw, Clock, LogOut, Trophy } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';

interface TournamentBustModalProps {
  visible: boolean;
  tournamentId?: string;       // used to stamp the "watch after bust" flag
  tournamentName?: string;
  buyInAmount: number;        // cost of one rebuy
  currency: 'playcoins' | 'pkcash';
  rebuyChips: number;          // chips restored on rebuy (= startingChips)
  walletBalance: number;       // caller's wallet balance in tournament currency
  lateRegEndAt?: Date | number | null;  // window cutoff
  currentBlindLevel?: number;
  lateRegLevels?: number;
  rebuysUsed?: number;
  rebuysAllowed?: boolean;     // can rebuy at all
  rebuying?: boolean;          // in-flight indicator
  finishPosition?: number | null; // recorded finishing place (from tournament.eliminations)
  wonAmount?: number | null;   // payout for that place; 0 = out of the money
  onRebuy: () => void;
  onLeave: () => void;
  onDismiss: () => void;       // close without action (player can stay seated as observer)
}

const TournamentBustModal: React.FC<TournamentBustModalProps> = ({
  visible,
  tournamentId,
  tournamentName,
  buyInAmount,
  currency,
  rebuyChips,
  walletBalance,
  lateRegEndAt,
  currentBlindLevel = 1,
  lateRegLevels = 4,
  rebuysUsed = 0,
  rebuysAllowed = true,
  rebuying = false,
  finishPosition = null,
  wonAmount = null,
  onRebuy,
  onLeave,
  onDismiss,
}) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [visible]);

  const cutoffMs = useMemo(() => {
    if (!lateRegEndAt) return null;
    if (typeof lateRegEndAt === 'number') return lateRegEndAt;
    return new Date(lateRegEndAt as any).getTime();
  }, [lateRegEndAt]);

  const remainingMs = cutoffMs ? Math.max(0, cutoffMs - now) : null;
  const levelInWindow = currentBlindLevel <= lateRegLevels;
  const windowOpen = rebuysAllowed && levelInWindow && (remainingMs === null || remainingMs > 0);
  const canAfford = walletBalance >= buyInAmount;
  const symbol = currency === 'pkcash' ? '' : '';
  const currencyLabel = currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins';

  const formatCountdown = (ms: number) => {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m >= 60) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      return `${h}h ${mm}m`;
    }
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  if (!visible) return null;

  return (
    <Box
      position="fixed"
      top={0} left={0} right={0} bottom={0}
      bg="rgba(0,0,0,0.85)"
      zIndex={9100}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
      onClick={onDismiss}
    >
      <Box
        bg="#0d1f2e"
        border="2px solid"
        borderColor={windowOpen ? POKER_COLORS.gold : '#475569'}
        borderRadius="2xl"
        boxShadow="0 25px 70px rgba(0,0,0,0.7)"
        p={7}
        maxW="440px"
        w="100%"
        onClick={e => e.stopPropagation()}
      >
        <VStack spacing={5} align="stretch">
          {/* Header */}
          <HStack spacing={3} align="center">
            <Box color="#ef4444">
              <Skull size={36} />
            </Box>
            <Box flex={1}>
              <Text color="#ef4444" fontSize="2xl" fontWeight="bold" lineHeight="1">
                Busted Out
              </Text>
              {tournamentName && (
                <Text color="gray.400" fontSize="xs" mt={1} noOfLines={1}>
                  {tournamentName}
                </Text>
              )}
            </Box>
            {windowOpen && (
              <Badge bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold} px={2} py={1} fontSize="xs">
                Rebuy open
              </Badge>
            )}
          </HStack>

          {/* Final result: finishing place + what it paid. Only once the
              rebuy window is closed - an open window means this bust may
              not be final yet. */}
          {!windowOpen && typeof finishPosition === 'number' && finishPosition > 0 && (
            <Box
              bg={(wonAmount || 0) > 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(71, 85, 105, 0.15)'}
              border="1px solid"
              borderColor={(wonAmount || 0) > 0 ? 'rgba(16, 185, 129, 0.5)' : '#475569'}
              borderRadius="md"
              p={4}
              textAlign="center"
            >
              <Text color="gray.400" fontSize="xs" letterSpacing="wide" fontWeight="bold" mb={1}>
                YOU FINISHED #{finishPosition}
              </Text>
              {(wonAmount || 0) > 0 ? (
                <Text color="#34d399" fontSize="2xl" fontWeight="bold" lineHeight="1.1">
                  You won {currency === 'playcoins'
                    ? `${(wonAmount || 0).toLocaleString()} PC`
                    : `$${(wonAmount || 0).toLocaleString()}`}
                </Text>
              ) : (
                <Text color="gray.300" fontSize="sm">Just outside the money this time.</Text>
              )}
            </Box>
          )}

          {/* Window status */}
          {windowOpen ? (
            <Box
              bg="rgba(245, 158, 11, 0.08)"
              border="1px solid"
              borderColor={`${POKER_COLORS.gold}55`}
              borderRadius="md"
              p={4}
            >
              <HStack spacing={2} mb={2}>
                <Clock size={16} color={POKER_COLORS.gold} />
                <Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="bold" letterSpacing="wide">
                  REBUY WINDOW
                </Text>
              </HStack>
              {remainingMs !== null && (
                <Text color="white" fontSize="xl" fontWeight="bold" lineHeight="1" mb={1}>
                  {formatCountdown(remainingMs)} left
                </Text>
              )}
              <Text color="gray.400" fontSize="xs">
                Level {currentBlindLevel} of {lateRegLevels} (rebuys close after level {lateRegLevels})
                {rebuysUsed > 0 && ` · You have used ${rebuysUsed} rebuy${rebuysUsed === 1 ? '' : 's'}`}
              </Text>
            </Box>
          ) : (
            <Box
              bg="rgba(71, 85, 105, 0.15)"
              border="1px solid"
              borderColor="#475569"
              borderRadius="md"
              p={4}
            >
              <Text color="gray.300" fontSize="sm">
                {!rebuysAllowed
                  ? 'This tournament does not allow rebuys.'
                  : !levelInWindow
                  ? `Rebuy window closed after level ${lateRegLevels} (you're on level ${currentBlindLevel}).`
                  : 'Rebuy window has closed.'}
              </Text>
            </Box>
          )}

          {/* Rebuy details */}
          {windowOpen && (
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.400" fontSize="sm">Cost</Text>
                <Text color="white" fontWeight="bold" fontSize="md">
                  {symbol}{buyInAmount.toLocaleString()} {currencyLabel}
                </Text>
              </HStack>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.400" fontSize="sm">You get back</Text>
                <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize="md">
                  {rebuyChips.toLocaleString()} chips
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.400" fontSize="sm">Wallet balance</Text>
                <Text color={canAfford ? 'white' : '#ef4444'} fontSize="md" fontWeight="medium">
                  {symbol}{walletBalance.toLocaleString()} {currencyLabel}
                </Text>
              </HStack>
            </Box>
          )}

          {/* Actions */}
          <VStack spacing={2} pt={1}>
            {windowOpen && (
              <Button
                w="100%"
                size="lg"
                h="52px"
                bg={canAfford ? POKER_COLORS.gold : '#374151'}
                color={canAfford ? POKER_COLORS.darkTeal : 'gray.400'}
                _hover={canAfford ? { bg: '#fde68a', transform: 'translateY(-1px)' } : undefined}
                _active={canAfford ? { transform: 'translateY(0)' } : undefined}
                leftIcon={<RotateCcw size={18} />}
                fontWeight="bold"
                fontSize="md"
                onClick={onRebuy}
                isLoading={rebuying}
                loadingText="Rebuying..."
                isDisabled={!canAfford || rebuying}
              >
                {canAfford
                  ? `Rebuy for ${symbol}${buyInAmount.toLocaleString()}`
                  : 'Insufficient balance'}
              </Button>
            )}
            <Button
              w="100%"
              variant="outline"
              borderColor="#475569"
              color="gray.300"
              _hover={{ bg: 'rgba(255,255,255,0.04)' }}
              leftIcon={<LogOut size={16} />}
              onClick={onLeave}
            >
              Leave tournament
            </Button>
            {windowOpen && (
              <Button
                w="100%"
                variant="ghost"
                color="gray.500"
                size="sm"
                leftIcon={<Trophy size={14} />}
                onClick={() => {
                  // Stamp a "watching after bust" flag for this tournament so
                  // GamePage's eliminated-overlay detection skips us as long
                  // as we're still on the table. Cleared when the user
                  // navigates to /tournaments. Without this the overlay
                  // popped immediately and "Watch the rest" took the user
                  // off the table - the opposite of what they asked for.
                  if (tournamentId) {
                    try { sessionStorage.setItem(`pss:watchAfterBust:${tournamentId}`, '1'); } catch {/* noop */}
                  }
                  onDismiss();
                }}
              >
                Watch the rest
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default TournamentBustModal;
